import { ElementRef, EventEmitter, Injectable } from '@angular/core';
import { max, min } from 'd3-array';
import { select, Selection } from 'd3-selection';
import {} from 'd3-transition';
import { zoom, ZoomBehavior, zoomIdentity, ZoomTransform } from 'd3-zoom';

import { IChartContext } from '../view-models/chart-viewmodel';

@Injectable()
export class Zoomer {
    // events

    cyclicFitFired = new EventEmitter<any>();

    // Data Members

    private readonly MAX_ZOOM_FACTOR = 25;

    private svg: Selection<any, unknown, null, undefined> | null = null;
    private context!: IChartContext;
    private zoom!: ZoomBehavior<Element, unknown>;
    private isOnMaxZoomLimit = false;

    // Properties

    zoomFactorChange = new EventEmitter<number>();
    private _factor: number = 1;
    get factor() {
        return this._factor;
    }

    set factor(factor: number) {
        if (this.svg == null || this._factor == factor) return;

        this._factor = factor;

        this.svg.call(this.zoom.scaleTo, factor);
    }

    // public functions

    init(element: ElementRef<any>, context: IChartContext) {
        this.context = context;
        let zoomer = this;

        this.svg = select(element.nativeElement);

        this.zoom = zoom()
            .scaleExtent([1, this.MAX_ZOOM_FACTOR])
            .filter(function (event) {
                if (event?.type == 'wheel') return zoomer.zoomOnWheelEnabled; // && event.ctrlKey; inserire anche il ctrl?

                return !event.ctrlKey && !event.button;
            })
            .on('zoom', function (event) {
                const transform = event.transform;

                if (!transform) return;

                transform.x = min([transform.x, 0]);
                transform.y = min([transform.y, 0]);
                transform.x = max([transform.x, (1 - transform.k) * element.nativeElement.clientWidth]);
                transform.y = max([transform.y, (1 - transform.k) * element.nativeElement.clientHeight]);

                context.transform = transform;

                if (!isNaN(transform.k)) zoomer.updateZoomFactor(transform.k);

                if (event.sourceEvent == null) zoomer.fitOnMaxZoomLimit();
            });

        this.svg.call(this.zoom);

        //// In order to disable double click and weel
        //this.svg.call(this.zoom).on("wheel.zoom", null)
        //    .on('dblclick.zoom', null);

        this.rescale();
    }

    zoomOnWheelEnabled = true;

    enableZoomOnWheel() {
        this.zoomOnWheelEnabled = true;
    }

    disableZoomOnWheel() {
        this.zoomOnWheelEnabled = false;
    }

    cleanup() {
        if (this.zoom) {
            this.zoom.on('zoom', null);
        }
    }

    rescale() {
        if (this.svg == null) return;

        this.svg.transition().duration(750).call(this.zoom.transform, zoomIdentity);
    }

    scale(scale: number) {
        if (this.svg == null) return;

        this.svg.call(this.zoom.transform, zoomIdentity.scale(scale));
    }

    transform(transform: ZoomTransform) {
        if (this.svg == null) return;

        this.svg.call(this.zoom.transform, transform);
    }

    // private functions

    private updateZoomFactor(factor: number) {
        this._factor = factor;
        this.zoomFactorChange.emit(factor);
    }

    private fitOnMaxZoomLimit() {
        if (this.factor >= this.MAX_ZOOM_FACTOR && this.isOnMaxZoomLimit) this.cyclicFitFired.emit();
        else this.isOnMaxZoomLimit = this.factor >= 10;
    }
}
